// src/services/dataCache.js
class DataCacheService {
  constructor() {
    this.cache = new Map();
    this.timers = new Map();
    this.lastRequestTime = new Map();
    this.subscribers = new Map(); // 新增：訂閱者
    this.REFRESH_INTERVAL = 30000; // 30秒
    this.IDLE_TIMEOUT = 30000; // 30秒無請求則停止
  }

  /**
   * 獲取資料（會觸發自動刷新機制）
   */
  async getData(key, fetchFunction) {
    const now = Date.now();
    this.lastRequestTime.set(key, now);

    // 如果快取中有資料且未過期，直接返回
    const cached = this.cache.get(key);
    if (cached && (now - cached.timestamp) < this.REFRESH_INTERVAL) {
      // 確保自動刷新計時器正在運行
      this.ensureAutoRefresh(key, fetchFunction);
      return cached.data;
    }

    // 獲取新資料
    try {
      const data = await fetchFunction();
      this.cache.set(key, {
        data,
        timestamp: now
      });

      // 啟動自動刷新
      this.ensureAutoRefresh(key, fetchFunction);
      
      return data;
    } catch (error) {
      // 如果請求失敗但有快取，返回快取資料
      if (cached) {
        return cached.data;
      }
      throw error;
    }
  }

  /**
   * 確保自動刷新計時器正在運行
   */
  ensureAutoRefresh(key, fetchFunction) {
    // 如果已有計時器，先清除
    if (this.timers.has(key)) {
      return; // 計時器已在運行
    }

    const timer = setInterval(async () => {
      const lastRequest = this.lastRequestTime.get(key);
      const now = Date.now();

      // 檢查是否超過閒置時間
      if (!lastRequest || (now - lastRequest) > this.IDLE_TIMEOUT) {
        // 停止自動刷新
        this.stopAutoRefresh(key);
        return;
      }

      // 自動刷新資料
      try {
        const data = await fetchFunction();
        this.cache.set(key, {
          data,
          timestamp: now
        });
        console.log(`[Cache] 自動刷新資料: ${key}`);
        
        // 通知訂閱者
        this.notifySubscribers(key, data);
      } catch (error) {
        console.error(`[Cache] 自動刷新失敗: ${key}`, error);
      }
    }, this.REFRESH_INTERVAL);

    this.timers.set(key, timer);
    console.log(`[Cache] 啟動自動刷新: ${key}`);
  }

  /**
   * 停止自動刷新
   */
  stopAutoRefresh(key) {
    const timer = this.timers.get(key);
    if (timer) {
      clearInterval(timer);
      this.timers.delete(key);
      console.log(`[Cache] 停止自動刷新: ${key}`);
    }
  }

  /**
   * 訂閱資料更新
   */
  subscribe(key, callback) {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    this.subscribers.get(key).add(callback);
    
    return () => {
      // 返回取消訂閱函數
      const callbacks = this.subscribers.get(key);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.subscribers.delete(key);
        }
      }
    };
  }

  /**
   * 通知訂閱者
   */
  notifySubscribers(key, data) {
    const callbacks = this.subscribers.get(key);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[Cache] 訂閱者回調錯誤: ${key}`, error);
        }
      });
    }
  }

  /**
   * 清除特定快取
   */
  clearCache(key) {
    this.cache.delete(key);
    this.lastRequestTime.delete(key);
    this.subscribers.delete(key);
    this.stopAutoRefresh(key);
  }

  /**
   * 清除所有快取
   */
  clearAll() {
    // 停止所有計時器
    this.timers.forEach((timer) => {
      clearInterval(timer);
    });
    
    this.cache.clear();
    this.timers.clear();
    this.lastRequestTime.clear();
    this.subscribers.clear();
    console.log('[Cache] 清除所有快取');
  }

  /**
   * 獲取快取狀態（用於調試）
   */
  getStatus() {
    const status = [];
    this.cache.forEach((value, key) => {
      const lastRequest = this.lastRequestTime.get(key);
      const hasTimer = this.timers.has(key);
      status.push({
        key,
        timestamp: value.timestamp,
        lastRequest,
        autoRefresh: hasTimer,
        age: Date.now() - value.timestamp
      });
    });
    return status;
  }
}

// 創建單例
const dataCacheService = new DataCacheService();

export default dataCacheService;

